"use client";

// A signed-in visitor's Sweet Coins: whether today's are waiting to be collected (the badge on the menu's
// Sweet Coin item) and their balance (the header's coin, components/coins/HeaderCoins.tsx). LibrarySync starts
// it for a signed-in visitor. The badge turns on by itself when the next day (UTC) begins, off when the coins
// are collected; both are asked again every minute while the tab is shown, when it comes back, and at once
// when something spends or adds coins (refreshCoins(), or a "sweet:coins" event from anywhere).

import { useSyncExternalStore } from "react";

const RECHECK_AFTER = 60_000;
/** The event that asks for the coins again at once (after buying, spending or collecting). */
export const COINS_EVENT = "sweet:coins";

/** Coins waiting to be collected today (0 when none). */
let waiting = 0;
let daily = 0;
let timer: number | undefined;
let lastCheck = 0;
/** The balance, null until known (or signed out). */
let balance: number | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function setBalance(value: number | null) {
  if (balance === value) return;
  balance = value;
  notify();
}

function set(value: number) {
  if (waiting === value) return;
  waiting = value;
  notify();
}

/** Turns the badge on when the next bonus is due. */
function onAt(msToNext: number) {
  window.clearTimeout(timer);
  timer = window.setTimeout(() => set(daily), Math.min(msToNext + 1000, 2 ** 31 - 1));
}

async function check() {
  lastCheck = Date.now();
  const res = await fetch("/api/me/coins", { cache: "no-store" });
  if (!res.ok) return;
  const data: { authenticated: false } | { authenticated: true; coins: number; msToNext: number; balance: number } = await res.json();
  if (!data.authenticated) {
    setBalance(null);
    return set(0);
  }
  setBalance(data.balance);
  set(data.coins);
  if (data.coins) daily = data.coins;
  else onAt(data.msToNext);
}

/** Starts watching for a signed-in visitor; returns the function that stops it. */
export function startCoinsBadge(): () => void {
  void check().catch(() => {});
  const visible = () => {
    if (document.visibilityState === "visible" && Date.now() - lastCheck > RECHECK_AFTER) void check().catch(() => {});
  };
  const now = () => void check().catch(() => {});
  const every = window.setInterval(visible, RECHECK_AFTER);
  document.addEventListener("visibilitychange", visible);
  window.addEventListener(COINS_EVENT, now);
  return () => {
    document.removeEventListener("visibilitychange", visible);
    window.removeEventListener(COINS_EVENT, now);
    window.clearInterval(every);
    window.clearTimeout(timer);
    set(0);
    setBalance(null);
  };
}

/** Coins were bought, spent or added: the header's balance (and the badge) asked again now. */
export function refreshCoins() {
  window.dispatchEvent(new Event(COINS_EVENT));
}

/** Just collected these coins: off until the next bonus. */
export function coinsCollected(coins: number, msToNext: number) {
  daily = coins;
  set(0);
  onAt(msToNext);
  void check().catch(() => {});
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Today's coins waiting to be collected, 0 when there are none. */
export function useCoinsWaiting(): number {
  return useSyncExternalStore(
    subscribe,
    () => waiting,
    () => 0,
  );
}

/** The signed-in visitor's Sweet Coins balance, or null until it's known. */
export function useCoinBalance(): number | null {
  return useSyncExternalStore(
    subscribe,
    () => balance,
    () => null,
  );
}
